import React, { act, useEffect, useRef, useState } from "react";
import { AlertCircle, AlertTriangle, Globe, Search } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Select } from "semantic-ui-react";
import {
  addCluster,
  clusterById,
  searchCluster,
  updateCluster,
} from "../../api/clusterNameApi";
import { getBulkDropdownData, getDropdownData } from "../../api/keyvalue";
import Grid from "../../components/Grid";
import LoadingSpinner from "../../components/LoadingSpinner";
import AlertForm from "../../components/alert/AlertForm";
import Button from "../../components/input/Button";
import SideDrawer from "../../components/input/SideDrawer";
import ClusterComponent from "../../components/master/ClusterComponent";
import {
  ActiveCellStyle,
  ResizableScrollingTextRenderer,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import { clusterValidation } from "../../components/validations/ClusterCreation";
import { errorActions } from "../../store/error-slice";
import Modal from "../../components/Modal";
import { FORMS, MODULES } from "../../const";
import DownloadGridReport from "../../components/DownloadGridReport";
import Tab from "../../components/Tab";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import useNextAndPreviousAuthority from "../../hooks/useNextAndPreviousAuthority";

const ClusterMaster = () => {
  const { firstViableTab, lastViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.CLUSTER,
  });

  const [drawerTab, setDrawerTab] = useState(firstViableTab);
  const errorState = useSelector((state) => state.error.clusterErrors);
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);

  const [tab, setTab] = useState(1);

  const auth = useSelector((state) => state.user.permissions[MODULES.Master]);
  const employeePermissions = auth[FORMS.CLUSTER.CLU.form];
  const downloadPermission = employeePermissions?.FORM?.DOWNLOAD;

  const { next, prev } = useNextAndPreviousAuthority({
    sectionKey: "cluster",
    minTab: 1,
    maxTab: 2,
    module: MODULES.Master,
    section: FORMS.CLUSTER,
    currentTab: tab,
    authenticated: true,
  });

  const [editMode, setEditMode] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);

  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchRes, setSearchRes] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [editCluster, setEditCluster] = useState({});
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // dropdown
  const [clusterStatusOpt, setClusterStatusOpt] = useState([]);
  const [clusterActiveOpt, setClusterActiveOpt] = useState([]);
  const [altActiveOpt, setAltActiveOpt] = useState([]);
  const [altStatusOpt, setAltStatusOpt] = useState([]);
  const [altTypeOpt, setAltTypeOpt] = useState([]);
  const [altEventOpt, setAltEventOpt] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  const deleteWarningRef = useRef();
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  // tab ref
  const clusterRef = useRef();
  const alertRef = useRef();

  //search ref
  const clusterNameRef = useRef(null);
  const clusterCodeRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  // popup
  const [showCloseButton, setShowCloseButton] = useState(false);
  const modalRef = useRef();
  const [tableData, setTableData] = useState([]);
  const [moduleName, setModuleName] = useState("cluster");

  const user = useSelector((state) => state.user);

  const handleDownloadPopUpChange = async () => {
    setShowCloseButton(true);
    setTableData(searchRes);
    modalRef.current?.showModal();
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      console.log("fetching dropdown");
      setIsSearchLoading(true);

      //new

      const {
        active,
        status,
        evn_type: evnTypeOpt,
        alert_type: alertTypeOpt,
      } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
        { key: "evn_type" },
        { key: "alert_type" },
      ]);

      setActiveOptions(active);
      setStatusOptions(status);
      setAltEventOpt(evnTypeOpt);
      setAltTypeOpt(alertTypeOpt);
      setClusterStatusOpt(status);
      setAltStatusOpt(status);
      setClusterActiveOpt(active);
      setAltActiveOpt(active);

      console.log("finished fetching dropdown");
      setIsLoading(false);
      setIsSearchLoading(false);
    })();
  }, []);
  const handleDrawerOpen = () => {
    dispatch(errorActions.clearClusterError());
    setIsDrawerOpen(true);
    setEditMode(false);
    setEditCluster(null);
    setDrawerTab(firstViableTab);
  };
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const reset = () => {
    alertRef.current.resetForm();
    clusterRef.current.resetForm();

    setEditMode(false);
    setEditCluster(null);
  };

  const nextButtonHandler = () => {
    switch (drawerTab) {
      case 1: {
        const cluErrors = clusterRef.current.validateForm();
        if (Object.keys(cluErrors).length > 0) {
          dispatch(
            errorActions.addClusterErrorObject({
              form: "clusterErrors",
              errors: cluErrors,
            }),
          );
          toast.error("Please fill required fields", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        } else {
          dispatch(errorActions.clearClusterError());
        }
        break;
      }
      default:
    }
    setDrawerTab(next());
  };

  const handleSubmit = async (e, bypassDeleteCheck) => {
    e.preventDefault();
    if (!bypassDeleteCheck && clusterRef.current.isDeletedActive()) {
      setPendingSubmitEvent(e);
      setShowDeleteWarning(true);
      deleteWarningRef.current?.showModal();
      return;
    }
    setSubmitted(true);
    setIsSubmitLoading(true);

    dispatch(errorActions.clearClusterError());

    const clusterErrors = clusterValidation(clusterRef.current.getData());

    if (Object.keys(clusterErrors).length > 0) {
      // setErrors({
      //   clusterErrors,
      //   hasErrors: true,
      // });
      dispatch(
        errorActions.validateCluster({
          clusterData: clusterRef.current.getData(),
        }),
      );
      setIsSubmitLoading(false);
      toast.error("There was an error adding Cluster", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    } else {
      if (editMode) {
        const alert = alertRef.current.getData();
        const modifiedAlert = alert.modified;
        const newAlert = alert.created;
        const unmodified = alert.unmodified;
        await updateCluster({
          clusterNameRequest: clusterRef.current.getData(),
          alert: alertRef.current.getData(),
          author: user.username,
          event: alertRef.current.getEvent(),
        });
        toast.success("Cluster update successfully!", {
          autoClose: 5000,
          containerId: "Main",
        });
      } else {
        await addCluster({
          cluster: clusterRef.current.getData(),
          alert: alertRef.current.getData(),
          author: user.username,
        });
        toast.success("Cluster added successfully!", {
          autoClose: 5000,
          containerId: "Main",
        });
      }
      setIsSubmitLoading(false);
      reset();
    }
  };

  const handleSearchReset = (e) => {
    e.preventDefault();
    clusterNameRef.current.value = "";
    clusterCodeRef.current.value = "";
    toDateRef.current.value = "";
    fromDateRef.current.value = "";
    setStatus("");
    setActive("");
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (tab === 1) {
      setActiveAccordion1(false);
      setResetVisible(true);
      setSearchClicked(true);
      setSearchSubmitLoading(true);
    }
    const response = await searchCluster({
      clnCluster: clusterNameRef.current.value,
      clnCode: clusterCodeRef.current.value,
      clnToDate: toDateRef.current.value,
      clnFromDate: fromDateRef.current.value,
      clnStatus: status,
      clnActive: active,
    });
    console.log(response);
    setSearchRes(response);
    setSearchSubmitLoading(false);
  };
  const deleteWarningModal = (
      <div style={{ padding: "20px" }}>
        <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AlertCircle color="red" size="25" />
          <p>Warning</p>
        </h3>
        <p>Active is set to Deleted. Are you sure you want to proceed?</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <Button
            label="Cancel"
            onClick={() => {
              deleteWarningRef.current?.close();
              setShowDeleteWarning(false);
              setPendingSubmitEvent(null);
            }}
          />
          <Button
            label="Proceed"
            className="ui red button"
            onClick={() => {
              deleteWarningRef.current?.close();
              setShowDeleteWarning(false);
              handleSubmit(pendingSubmitEvent, true);
              setPendingSubmitEvent(null);
            }}
          />
        </div>
      </div>
    );

  return (
    <>
      <Modal ref={modalRef} closeButton={showCloseButton}>
        <DownloadGridReport
          action="Custom Cluster Excel Download"
          tableData={tableData}
          moduleName={moduleName}
        />
      </Modal>

      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div className="ui tabular menu attached">
          <div
            className={`item ${tab === 1 ? "active" : ""}`}
            onClick={() => {
              setTab(1);
            }}
          >
            <span>
              <Globe size={15} style={{ marginRight: "5px" }} />
            </span>
            Cluster Search
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0px",
          }}
          className="ui tabular menu top attached"
        >
          <Button
            template="drawerOpen"
            disabled={firstViableTab === -1}
            onClick={handleDrawerOpen}
            authenticated={true}
            form={FORMS.CLUSTER.CLU.form}
            module={MODULES.Master}
            editMode={false}
          />
        </div>
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              <i
                className="refresh icon primary button"
                style={{
                  display: `${resetVisible ? "flex" : "none"}`,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  // handleReset(e);
                }}
              ></i>
            </div>
          </div>
          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            {isSearchLoading ? (
              <LoadingSpinner />
            ) : (
              <form className="ui form">
                <div className="four fields">
                  <div className="field">
                    <label>Cluster Name</label>
                    <input
                      type="text"
                      placeholder="Cluster Name"
                      name="stakeholderName"
                      ref={clusterNameRef}
                    />
                  </div>
                  <div className="field">
                    <label>Cluster Code</label>
                    <input
                      type="text"
                      placeholder="Cluster Code"
                      name="clusterCode"
                      ref={clusterCodeRef}
                    />
                  </div>
                  <div className="field">
                    <label>From Date</label>
                    <input
                      type="date"
                      name="clusterExpiaryDate"
                      placeholder="From Date"
                      ref={fromDateRef}
                    />
                  </div>
                  <div className="field">
                    <label>To Date</label>
                    <input
                      type="date"
                      name="clusterExpiaryDate"
                      placeholder="To Date"
                      ref={toDateRef}
                    />
                  </div>
                  {/*<div className="field">*/}
                  {/*    <label>Stakeholder</label>*/}
                  {/*    <Select*/}
                  {/*        placeholder="Stakeholder"*/}
                  {/*        options={empStakeholderOpt}*/}
                  {/*        value={empStakeholder}*/}
                  {/*        style={{height: "20px"}}*/}
                  {/*        onChange={(e, data) => {*/}
                  {/*            setEmpStakeholder(data.value);*/}
                  {/*        }}*/}
                  {/*    />*/}
                  {/*</div>*/}
                  {/*<div className="field">*/}
                  {/*    <label>NIC</label>*/}
                  {/*    <input*/}
                  {/*        type="text"*/}
                  {/*        placeholder="NIC"*/}
                  {/*        name="stakeholderCode"*/}
                  {/*        ref={nicRef}*/}
                  {/*    />*/}
                  {/*</div>*/}
                  {/*<div className="field">*/}
                  {/*    <label>Category</label>*/}
                  {/*    <Select*/}
                  {/*        options={empCategoryOpt}*/}
                  {/*        placeholder="Category"*/}
                  {/*        style={{height: "20px"}}*/}
                  {/*        value={empCategory}*/}
                  {/*        onChange={(e, data) => {*/}
                  {/*            setEmpCategory(data.value);*/}
                  {/*        }}*/}
                  {/*    />*/}
                  {/*</div>*/}
                </div>

                <div className="four fields">
                  {/*<div className="field">*/}
                  {/*    <label>Inspection Date</label>*/}
                  {/*    <input*/}
                  {/*        type="date"*/}
                  {/*        name="InspectionDate"*/}
                  {/*        placeholder="To Date"*/}
                  {/*        ref={inspectionDateRef}*/}
                  {/*    />*/}
                  {/*</div>*/}
                  {/*<div className="field">*/}
                  {/*    <label>from Date</label>*/}
                  {/*    <input*/}
                  {/*        type="date"*/}
                  {/*        name="empExpiaryDate"*/}
                  {/*        placeholder="To Date"*/}
                  {/*        ref={fromDateRef}*/}
                  {/*    />*/}
                  {/*</div>*/}
                  {/*<div className="field">*/}
                  {/*    <label>To Date</label>*/}
                  {/*    <input*/}
                  {/*        type="date"*/}
                  {/*        name="empExpiaryDate"*/}
                  {/*        placeholder="To Date"*/}
                  {/*        ref={toDateRef}*/}
                  {/*    />*/}
                  {/*</div>*/}

                  <div className="field">
                    <label>Status</label>
                    <Select
                      options={statusOptions}
                      placeholder="Status"
                      style={{ height: "20px" }}
                      value={status}
                      onChange={(e, data) => {
                        setStatus(data.value);
                      }}
                    />
                  </div>

                  <div className="field">
                    <label>Active</label>
                    <Select
                      options={activeOptions}
                      placeholder="Active"
                      style={{ height: "20px" }}
                      value={active}
                      onChange={(e, data) => {
                        setActive(data.value);
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div className="field" style={{ width: "100%" }}>
                    <Button
                      template="search"
                      onClick={(e) => {
                        handleSearchSubmit(e);
                      }}
                    />
                    <Button
                      template="searchReset"
                      onClick={(e) => {
                        handleSearchReset(e);
                      }}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                heading="Cluster"
                downloadPopUpScreen={true}
                showDownloadButton={downloadPermission}
                downloadParam={""}
                downloadAction="Cluster Excel Download"
                isLoading={searchFetchLoading}
                handleDownloadPopUpChange={handleDownloadPopUpChange}
                content={searchRes}
                config={[
                  {
                    headerName: "Id",
                    field: "clnId",
                    sort: "desc",
                    hide: true,
                  },
                  {
                    headerName: "Code",
                    field: "clnCode",
                  },
                  {
                    headerName: "Name",
                    field: "clnCluster",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Status",
                    field: "clnStatus",
                    cellRenderer: StatusCellStyle,
                  },
                  {
                    headerName: "Active",
                    field: "clnActive",
                    cellRenderer: ActiveCellStyle,
                  },
                ]}
                onCellDoubleClicked={async (props) => {
                  setSearchFetchLoading(true);
                  const response = await clusterById(props.data.clnId);
                  console.log("-------");
                  console.log(response);
                  setEditCluster(response);
                  setEditMode(true);
                  setIsDrawerOpen(true);
                  setSearchFetchLoading(false);
                }}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <>
              <div
                className="cluster-code-header"
                style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
              >
                <h3>Cluster Code: {editCluster?.cluster?.clnCode}</h3>
              </div>
            </>
          ) : null
        }
        body={
          <>
          <form className="drawer-content" onSubmit={handleSubmit}>
            <div style={{ width: "60vw", boxShadow: "none" }}>
              <div className="ui top attached tabular menu">
                <Tab
                  Icon={Globe}
                  onClick={() => {
                    setDrawerTab(1);
                  }}
                  active={drawerTab === 1}
                  errors={Object.keys(errorState.clusterErrors).length !== 0}
                  label="Cluster"
                  authenticated={true}
                  form={FORMS.CLUSTER.CLU_DETAIL.form}
                  showMandatory={true}
                  isMandatory={true}
                />
                <Tab
                  Icon={AlertTriangle}
                  onClick={() => {
                    setDrawerTab(2);
                  }}
                  active={drawerTab === 2}
                  errors={Object.keys(errorState.alertErrors).length !== 0}
                  label="Alert"
                  authenticated={true}
                  form={FORMS.CLUSTER.CLU_ALERT.form}
                />
              </div>
              <div
                className="ui bottom attached active tab segment"
                style={{ display: drawerTab === 1 ? "block" : "none" }}
              >
                <ClusterComponent
                  ref={clusterRef}
                  isLoading={isLoading}
                  errors={errorState.clusterErrors}
                  dropdown={{
                    clusterStatusOpt,
                    clusterActiveOpt,
                  }}
                  editMode={editMode}
                  currentValues={editMode ? editCluster.cluster : null}
                />
              </div>
              <div
                className="ui bottom attached active tab segment"
                style={{ display: drawerTab === 2 ? "block" : "none" }}
              >
                <AlertForm
                  opeType={2}
                  ref={alertRef}
                  errors={errorState.alertErrors}
                  dropdown={{
                    altActiveOpt,
                    altStatusOpt,
                    altTypeOpt,
                    altEventOpt,
                  }}
                  isLoading={isLoading}
                  editMode={editMode}
                  module={MODULES.Master}
                  currentValues={editMode ? editCluster.alert : null}
                />
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {drawerTab > firstViableTab && (
                    <Button
                      template="back"
                      tab={tab - 1}
                      module={MODULES.Master}
                      section={FORMS.CLUSTER}
                      authenticated={true}
                      onClick={() => setDrawerTab(prev())}
                    />
                  )}
                  {drawerTab < lastViableTab && (
                    <Button
                      template="next"
                      tab={tab + 1}
                      module={MODULES.Master}
                      section={FORMS.CLUSTER}
                      authenticated={true}
                      onClick={nextButtonHandler}
                    />
                  )}
                  {editMode ? (
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isSubmitLoading}
                      create={!editMode}
                      form={FORMS.CLUSTER.CLU.form}
                      module={MODULES.Master}
                      section={FORMS.CLUSTER}
                      authenticated={true}
                      editMode={editMode}
                    />
                  ) : drawerTab === lastViableTab && !isLoading ? (
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isSubmitLoading}
                      create={!editMode}
                      form={FORMS.CLUSTER.CLU.form}
                      module={MODULES.Master}
                      section={FORMS.CLUSTER}
                      authenticated={true}
                      editMode={editMode}
                    />
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>
          </form>
          <Modal ref={deleteWarningRef} closeButton={false} minWidth={"300px"}>
                  {showDeleteWarning && deleteWarningModal}
                </Modal>
          </>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};

export default ClusterMaster;
